/**
 * sceneNumber — null-safe scene_number display & sort helpers.
 *
 * scene_number is a Union[int, str, None] coming from the backend. Scenes
 * whose PDF slugline did not carry a parseable leading integer land in the
 * DB with scene_number === null and MUST NOT render as "null" / "undefined"
 * / "0" anywhere in the UI.
 *
 * The API takes the RAW value (not a scene object) so call sites can pass
 * any of: scene.scene_number, beat.scene_number, item.sceneNum, etc.
 *
 * Usage:
 *   import {
 *     formatSceneNumber,
 *     sortSceneNumber,
 *     countSceneNumbers,
 *   } from "../utils/sceneNumber";
 *
 *   <span>{formatSceneNumber(scene.scene_number)}</span>                 // "7" or "—"
 *   <span>{formatSceneNumber(n, { padStart: 2 })}</span>                  // "07" / "—"
 *   <span>{formatSceneNumber(n, { unnumberedLabel: "(unnumbered)" })}</span>
 *   <span>{formatSceneNumber(n, { prefix: "#" })}</span>                  // "#7" / "—"
 *
 *   scenes.sort((a, b) => sortSceneNumber(a.scene_number, b.scene_number));
 *   const { numbered, unnumbered } = countSceneNumbers(scenes);
 */

/**
 * Returns true when a raw scene_number value is "missing" — null, undefined,
 * or the empty string. All other falsy values (0) are valid.
 */
export function isUnnumbered(n) {
  return n === null || n === undefined || n === "";
}

/**
 * Render a raw scene_number value for display.
 *
 * @param {number|string|null|undefined} n
 * @param {object} [opts]
 * @param {string} [opts.unnumberedLabel="—"]  — what to render when missing.
 *                                                For call sheets: "(unnumbered)".
 * @param {number} [opts.padStart]            — pad numeric values with leading
 *                                                zeros to this width.
 * @param {string} [opts.prefix=""]           — prepend to the numbered value
 *                                                (e.g. "#"). NOT applied to
 *                                                the unnumbered label.
 * @returns {string}
 */
export function formatSceneNumber(n, opts = {}) {
  if (isUnnumbered(n)) {
    return opts.unnumberedLabel || "—";
  }
  let out = String(n);
  // Defensive: strip trailing ".0" / ".00" if a backend path slips a
  // string-encoded integer-valued float through (e.g. "19.0" from an
  // openpyxl Excel cell that didn't go through normalize_scene_number).
  const dotZeroMatch = out.match(/^(-?\d+)\.0+$/);
  if (dotZeroMatch) {
    out = dotZeroMatch[1];
  }
  if (opts.padStart && /^\d+$/.test(out)) {
    out = out.padStart(opts.padStart, "0");
  }
  if (opts.prefix) {
    out = opts.prefix + out;
  }
  return out;
}

/**
 * Comparator for raw scene_number values. Null/empty sort to the END.
 * Suffix-form numbers ("77a", "77b") sort lexicographically after the same
 * integer base. Pure-string values sort lexicographically at the end of the
 * integer range.
 *
 * Use:  scenes.sort((a, b) => sortSceneNumber(a.scene_number, b.scene_number));
 */
export function sortSceneNumber(a, b) {
  const aMissing = isUnnumbered(a);
  const bMissing = isUnnumbered(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  const parse = (v) => {
    const m = String(v).match(/^(\d+)([A-Za-z]?)$/);
    return m ? [parseInt(m[1], 10), m[2] || ""] : [Number.MAX_SAFE_INTEGER, String(v)];
  };
  const [ia, sa] = parse(a);
  const [ib, sb] = parse(b);
  if (ia !== ib) return ia - ib;
  return sa.localeCompare(sb);
}

/**
 * Count numbered vs unnumbered scenes in a list.
 *
 * @returns {{ numbered: number, unnumbered: number }}
 */
export function countSceneNumbers(scenes) {
  let numbered = 0;
  let unnumbered = 0;
  for (const s of scenes || []) {
    if (s && !isUnnumbered(s.scene_number)) numbered++;
    else unnumbered++;
  }
  return { numbered, unnumbered };
}

/**
 * Render a scene-count badge. When all scenes are numbered (the common case)
 * returns the plain total for backward-compatibility. When at least one scene
 * is unnumbered, returns "X numbered + Y unnumbered".
 */
export function formatSceneCountBadge(scenes) {
  const { numbered, unnumbered } = countSceneNumbers(scenes);
  if (unnumbered === 0) return String(numbered);
  return `${numbered} numbered + ${unnumbered} unnumbered`;
}
